import React, { useEffect, useState } from "react";
import * as R from "ramda";
import { string, object, shape, array, node, number, bool } from "prop-types";
import SubGroupSelect from "../Select/SubGroupSelect.jsx";
import Select from "../Select/Select.jsx";
import cx from "classnames";
import MultiSelect from "../MultiSelect/MultiSelect.jsx";
import Dropdown from "../Dropdown/Dropdown.jsx";
import LineChart from "../LineChart/LineChart.jsx";
import TrendsTable from "../LineChart/TrendsTable.jsx";
import Indicators from "../Indicators/Indicators.jsx";
import { lookupValueById } from "../../common/helpers.js";
import { useChartData } from "../../common/ChartData.jsx";
import { scaleOrdinal } from "d3-scale";
import queryString from "query-string";
import useBoundingClientRect from "../../common/useBoundingClientRect.js";
import { COLORS } from "../../common/chart.js";
import useFetchData from "../../common/useFetchData.js";
import Container100 from "../Container/Container100.jsx";
import Svg from "../Svg/Svg.jsx";
import Section from "../Section/Section.jsx";
import Footnotes from "../Footnotes/Footnotes.jsx";

import "../LineChart/line-chart-section.scss";
import "../DataExplorerTable/data-explorer-table.scss";
import "./chart-content.scss";

const propTypes = {
  chartProps: shape({
    page: string,
    lastAnalyzed: string,
    lineChartTitle: node,
    datasetInfo: object,
    lines: object,
    defaultInitialIndicator: bool,
    indicatorItems: array,
    trendsTableTitle: array,
    defaultIndicator: number,
    locationId: string,
    formattedFootnotes: array,
  }),
  defaultBreakdownLabel: string,
};

const ChartContent = (props) => {
  const {
    lastAnalyzed,
    lineChartTitle,
    trendTableTitle,
    datasetInfo,
    page,
    indicatorItems,
    trendsTableTitle,
    defaultIndicator,
    locationId,
    formattedFootnotes,
  } = props.chartProps;

  const { defaultBreakdownLabel } = props;

  const { chartData, setChartData, filterData, apiUrl } = useChartData();

  if (chartData.indicator === 0) {
    setChartData({ indicator: defaultIndicator });
  }
  const [lines, setLines] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const filters = R.when(
    (page) => R.equals(page, "department"),
    R.compose(
      R.values,
      R.filter((i) => {
        return i.filterId !== "location";
      }),
    ),
  )(filterData.data);

  const fullIndicator = R.add(
    R.prop("indicator")(chartData),
    R.prop("subdistributionclass")(chartData),
  );

  const queryObj = {
    ...chartData,
    indicator: fullIndicator,
    locationid: locationId || chartData?.locationid,
  };

  const querystring = queryString.stringify(queryObj, { arrayFormat: "comma" });

  const { data, isloading, error } = useFetchData(
    `${apiUrl}/line-chart/${
      page === "data-explorer"
        ? parseInt(chartData.subdistributionclass)
          ? "locationsubdistribution"
          : "locations"
        : "subdistribution"
    }/?${querystring}`,
    {
      method: "GET",
    },
  );

  // console.log(
  //   `${apiUrl}/line-chart/${
  //     page === "data-explorer"
  //       ? parseInt(chartData.subdistributionclass)
  //         ? "locationsubdistribution"
  //         : "locations"
  //       : "subdistribution"
  //   }/?${querystring}`,
  // );

  //filter the filters to retrieve current values
  const getValues = (v, k) => {
    const filterOpts =
      k === "subdistributionid"
        ? R.compose(R.unnest, R.pluck("options"))(R.pathOr([], [2], filters))
        : R.compose(
            R.unnest,
            R.pluck("options"),
            R.filter(R.prop("filterId")),
          )(filters);
    return R.compose(
      R.pluck("value"),
      R.unnest,
      R.map((i) => R.filter((x) => String(x.id) === String(i))(filterOpts)),
    )(R.flatten([v]));
  };

  const readableState = `'${R.compose(
    R.join(", "),
    R.flatten,
    R.values,
    R.mapObjIndexed(getValues),
    R.reject(R.isNil),
    R.pickAll(["locationid", "timeframeid", "subdistributionid"]),
  )(chartData)}${
    chartData.subdistributionReadableName && page === "department"
      ? `, ${chartData.subdistributionReadableName}`
      : ""
  }'`;

  const filterItems = (items, indicator) => {
    return indicator === 300
      ? R.filter((x) => x.filterId !== "0" && x.filterId !== "2")(items)
      : R.filter((x) => x.filterId !== "1" && x.filterId !== "3")(items);
  };

  const [{ width: lineChartWidth }, lineChartContainerRef] =
    useBoundingClientRect();

  useEffect(() => {
    if (!data?.lines) {
      return [];
    }
    setLines(() => {
      if (!data?.lines) {
        return [];
      }
      const colorScale = scaleOrdinal()
        .domain(R.pluck("id", data.lines))
        .range(COLORS);

      return data.lines.map((line) => ({
        ...line,
        color: colorScale(line.id),
      }));
    });
  }, [data, chartData]);

  const findSelectedItem = (item, array) => {
    R.find(R.propEq("subdistributionClass", item), array);
    return R.find(R.propEq("subdistributionClass", item), array) || null;
  };

  return chartData ? (
    <Container100 className="data-explorer__charts">
      <p className="data-explorer__updated-text">
        Last updated: {lastAnalyzed}
      </p>
      <div className="chart__container chart__container--line-chart">
        <div className="data-explorer__additional-filters">
          {filters &&
            filters.length > 0 &&
            filters.map((item) => {
              //we want to skip the location option on dept page
              if (item.filterId === "location" && page === "department")
                return false;

              if (R.type(item) === "Array") {
                //Grouped Dropdown

                return (
                  <React.Fragment
                    key={JSON.stringify({
                      ...chartData.fullIndicator,
                      ...chartData.subdistributionid,
                    })}
                  >
                    {page === "data-explorer" ? (
                      <SubGroupSelect
                        key={JSON.stringify(item)}
                        placeholder={"Additional indicator"}
                        label={"Additional indicator"}
                        className="select--grouped"
                        itemToId={R.prop("id")}
                        onChange={(change, subdistributionclass) => {
                          const readableName = R.filter(
                            R.propEq(
                              "subdistributionClass",
                              subdistributionclass,
                            ),

                            filters[2],
                          )[0].filterDefaultValue;
                          setChartData({
                            subdistributionid: change?.id,
                            subdistributionclass: subdistributionclass,
                            subdistributionReadableName: readableName,
                          });
                          setSelectedItem(change);
                        }}
                        activeSubdistributionClass={
                          chartData.subdistributionclass
                        }
                        selectProps={{
                          items: filterItems(item, chartData.indicator),
                          itemToString: R.prop("value"),
                          selectedItem: selectedItem,
                        }}
                      />
                    ) : (
                      <Select
                        key={JSON.stringify(item)}
                        updateBtnText={true}
                        placeholder="Indicator"
                        hideClear={true}
                        onChange={(change) => {
                          setChartData({
                            subdistributionReadableName:
                              change.filterDefaultValue,
                            subdistributionclass: change.subdistributionClass,
                          });
                          setSelectedItem(
                            findSelectedItem(
                              change.subdistributionclass,
                              filterItems(item, chartData.indicator),
                            ),
                          );
                        }}
                        activeSubdistributionClass={
                          chartData.subdistributionclass
                        }
                        selectProps={{
                          selectedItem: findSelectedItem(
                            chartData.subdistributionclass,
                            filterItems(item, chartData.indicator),
                          ),
                          items: [...filterItems(item, chartData.indicator)],
                          itemToString: R.prop("filterDefaultValue"),
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              } else {
                if (!item?.options?.length) return false;

                //location ansetd timeframe filters
                const items = item.options;
                const filterId = String(`${item.filterId}id`).toLowerCase();
                const facetIds = `${R.pluck("id", item.options || [])}`;
                const isTimeframe = filterId === "timeframeid";
                return (
                  <MultiSelect
                    icon={<Svg id={filterId} />}
                    // key={JSON.stringify(item)}
                    placeholder={item.filterDefaultValue}
                    selectRange={isTimeframe}
                    filterId={filterId}
                    selectProps={{
                      items: items,
                      itemToString: R.prop("value"),
                      activeItems: chartData[filterId] || [],
                    }}
                    onChange={(change) => {
                      const selectedItems = R.compose(
                        R.concat(R.pluck("id", change)),
                        R.reject((id) => facetIds.includes(id)),
                      )(chartData[item.filterDefaultId] || []);

                      if (isTimeframe) {
                        const lookup = R.map((item) =>
                          lookupValueById("id", item, items),
                        )(selectedItems);
                        const vals = R.map(R.prop("value"))(lookup);
                        const max = Math.max(...vals);
                        const min = Math.min(...vals);
                        const range = R.filter(
                          (i) =>
                            Number(R.prop("value")(i)) >= min &&
                            Number(R.prop("value")(i)) <= max,
                        )(items);
                        setChartData({
                          [filterId]: R.map(R.prop("id"))(range),
                        });
                      } //
                      else {
                        setChartData({
                          [filterId]: selectedItems,
                        });
                      }
                    }}
                  />
                );
              }
            })}
        </div>
        {readableState !== "''" && (
          <div className="current-filters">
            <p className="current-filters__header">Displaying</p>
            <p className="current-filters__filters">{readableState}</p>
          </div>
        )}
        <div className="department__title-container department__graph-container ">
          {lineChartTitle}
          <p>{data?.indicator?.name}</p>
        </div>
        <Indicators
          items={indicatorItems}
          active={chartData?.indicator}
          page
          handleClick={(change) => {
            setChartData({
              indicator: parseInt(change),
              subdistributionclass: 0, //reset subdistributionclass on indicator change
              subdistributionid: null, //reset subdistributionid on indicator change
              subdistributionReadableName: null,
            });
            setSelectedItem(null);
          }}
        />
      </div>
      <Container100 className="data-explorer__charts">
        <div
          className={cx("line-chart-section", {
            "line-chart-section--loading": isloading,
          })}
        >
          {isloading ? (
            <div className="line-chart-section__loading">Loading...</div>
          ) : error ? (
            <div className="line-chart-section__error">{error}</div>
          ) : null}
          {lines ? (
            <>
              <div
                className="line-chart-section__line-chart"
                ref={lineChartContainerRef}
              >
                {lineChartWidth && (
                  <LineChart
                    lines={lines}
                    width={lineChartWidth}
                    height={Math.min(600, lineChartWidth * (2 / 3))}
                  />
                )}
              </div>
              <Dropdown button={datasetInfo.buttonText}>
                <div>
                  {datasetInfo.content &&
                    datasetInfo.content.map((text, i) => (
                      <div key={i}>{text}</div>
                    ))}
                </div>
              </Dropdown>
              {/* {trendTableTitle && (
                <Section
                  aos={true}
                  theme="light"
                  title={trendTableTitle}
                  // subtitle={data?.indicator?.name}
                  modifier="section--text-left"
                  className="data-explorer__section data-explorer__wrapper department__title-container "
                  containerModifier="section__container--b-0"
                />
              )} */}
            </>
          ) : null}
        </div>
      </Container100>
      {lines && data?.indicator && (
        <Section
          aos={true}
          modifier="section--text-left"
          className=" trends-table__section department__title-container "
          title={trendsTableTitle || trendTableTitle}
          containerModifier="section__container--b-0"
          theme="light"
        >
          <TrendsTable
            lines={lines}
            indicator={data?.indicator || {}}
            breakdownLabel={
              chartData.subdistributionReadableName || defaultBreakdownLabel
            }
          />
          {formattedFootnotes && <Footnotes footnotes={formattedFootnotes} />}
        </Section>
      )}
    </Container100>
  ) : (
    false
  );
};

ChartContent.propTypes = propTypes;
export default ChartContent;
